//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PinterestConnectionStatus } from '@bvengineering/pinterest-gateway-service-types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { IPinterestSettings } from '@account-settings/redux/pinterest/pinterestSettingsModel';
import * as pinterestOAuthApi from '@apis/pinterest-oauth/pinterestOAuthApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { getPinterestIsFetchingSettings } from '@pinterest/redux/selectors/getPinterestSettings';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';

const initialState: IPinterestSettings = {
  errors: {
    fetchSettingsError: undefined,
    connectError: undefined,
    disconnectError: undefined,
    reconnectError: undefined,
  },
  flags: {
    isFetchingSettings: false,
    isConnecting: false,
    isDisconnecting: false,
    isReconnecting: false,
  },
  username: undefined,
  oauthEndpoint: undefined,
  isConnected: false,
  hasStaleCredentials: false,
  shouldShowPermissions: false,
  scope: undefined,
};

const { actions, reducer } = createSlice({
  name: SliceNames.AccountSettingsPinterest,
  initialState,
  reducers: {
    requestPinterestSettings(state) {
      state.flags.isFetchingSettings = true;
      state.errors.fetchSettingsError = undefined;
    },
    recievePinterestSettingsSucceeded(
      state,
      action: PayloadAction<PinterestConnectionStatus>,
    ) {
      state.isConnected = action.payload.isConnected;
      state.username = action.payload.username;
      state.hasStaleCredentials = action.payload.hasStaleCredentials;
      state.shouldShowPermissions = action.payload.shouldShowPermissions;
      state.scope = action.payload.scope;
      state.flags.isFetchingSettings = false;
    },
    recievePinterestSettingsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingSettings = false;
      state.errors.fetchSettingsError = action.payload;
    },
    requestPinterestOAuthEndpoint(state) {
      state.flags.isConnecting = true;
      state.errors.connectError = undefined;
    },
    receivePinterestOAuthEndpointSucceeded(
      state,
      action: PayloadAction<string>,
    ) {
      state.oauthEndpoint = action.payload;
      state.flags.isConnecting = false;
    },
    receivePinterestOAuthEndpointFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isConnecting = false;
      state.errors.connectError = action.payload;
    },
    requestDeAuthPinterest(state) {
      state.flags.isDisconnecting = true;
      state.errors.disconnectError = undefined;
    },
    recieveDeAuthPinterestSucceeded(state) {
      state.isConnected = false;
      state.username = undefined;
      state.hasStaleCredentials = false;
      state.flags.isDisconnecting = false;
    },
    recieveDeAuthPinterestFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isDisconnecting = false;
      state.errors.disconnectError = action.payload;
    },
    requestReconnectPinterest(state) {
      state.flags.isReconnecting = true;
      state.errors.reconnectError = undefined;
    },
    recieveReconnectPinterestSucceeded(state, action: PayloadAction<string>) {
      state.flags.isReconnecting = false;
      state.oauthEndpoint = action.payload;
    },
    recieveReconnectPinterestFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isReconnecting = false;
      state.errors.reconnectError = action.payload;
    },
  },
});

export const getPinterestSettings = createThunk(
  () => async (dispatch, getState) => {
    const isFetchingSettings = getPinterestIsFetchingSettings(getState());
    if (isFetchingSettings) {
      return;
    }

    dispatch(actions.requestPinterestSettings());
    try {
      const response = await pinterestOAuthApi.getConnectionStatus();
      dispatch(actions.recievePinterestSettingsSucceeded(response));
    } catch (_e) {
      dispatch(actions.recievePinterestSettingsFailed());
    }
  },
);

export const connectPinterest = createThunk(() => async (dispatch) => {
  dispatch(actions.requestPinterestOAuthEndpoint());

  try {
    const url = await pinterestOAuthApi.getOAuthUrl();
    dispatch(actions.receivePinterestOAuthEndpointSucceeded(url));
  } catch (_e) {
    dispatch(actions.receivePinterestOAuthEndpointFailed());
  }
});

export const disconnectPinterest = createThunk(() => async (dispatch) => {
  dispatch(actions.requestDeAuthPinterest());

  try {
    await pinterestOAuthApi.revokeToken();
    dispatch(actions.recieveDeAuthPinterestSucceeded());

    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'Pinterest account successfully disconnected!',
        type: ToastType.SUCCESS,
      }),
    );
  } catch (e) {
    dispatch(actions.recieveDeAuthPinterestFailed());

    dispatch(
      toastNotificationsActions.addToastNotification({
        title:
          'An error occurred when trying to disconnect your Pinterest account.',
        type: ToastType.ERROR,
      }),
    );
  }
});

export const reconnectPinterest = createThunk(() => async (dispatch) => {
  dispatch(actions.requestReconnectPinterest());

  try {
    await pinterestOAuthApi.revokeToken();
    const url = await pinterestOAuthApi.getOAuthUrl();
    dispatch(actions.recieveReconnectPinterestSucceeded(url));
  } catch (_e) {
    dispatch(actions.recieveReconnectPinterestFailed());

    dispatch(
      toastNotificationsActions.addToastNotification({
        title:
          'An error occurred when trying to reconnect your Pinterest account.',
        type: ToastType.ERROR,
      }),
    );
  }
});

export default reducer;
